import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import api from '../utils/api';
import { Search, Eye } from 'lucide-react';
import InvitationCard from '../components/InvitationCard';

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    api
      .get('/templates')
      .then((res) => setTemplates(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(templates.map((t) => t.category)))],
    [templates]
  );

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Invitation Templates</h1>
        <p className="text-gray-600 mt-1">
          Choose a template for your event invitations. Names and details shown are demo content.
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search templates..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'all' ? 'All' : category}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredTemplates.map((template) => (
          <Card key={template.id} className="overflow-hidden hover:shadow-lg transition group">
            <button
              type="button"
              className="relative aspect-[3/4] w-full overflow-hidden block text-left"
              onClick={() => setPreviewTemplate(template)}
            >
              <InvitationCard template={template} />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                <span className="flex items-center gap-1.5 text-white text-xs font-medium bg-black/40 px-3 py-1.5 rounded-full">
                  <Eye className="w-3.5 h-3.5" />
                  Preview
                </span>
              </div>
            </button>
            <div className="p-4">
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: template.colorFrom }}
                />
                <h3 className="font-semibold">{template.name}</h3>
              </div>
              <p className="text-sm text-gray-600 mt-1 ml-4">{template.category}</p>
              <Button
                className="w-full mt-3 bg-orange-500 hover:bg-orange-600"
                size="sm"
                onClick={() => navigate('/dashboard/events/create')}
              >
                Use Template
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-gray-500">No templates found</p>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => !open && setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="w-full sm:w-56 aspect-[3/4] rounded-lg overflow-hidden border shrink-0 mx-auto">
                <InvitationCard template={previewTemplate} />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-lg font-semibold">{previewTemplate.name}</h3>
                  <p className="text-sm text-gray-600">Category: {previewTemplate.category}</p>
                </div>
                <p className="text-sm text-gray-600">
                  Description: Request the honour of your presence.
                </p>
                <p className="text-xs text-gray-400">
                  Names, dates, and details shown are demo content for preview only.
                </p>
                <Button
                  className="bg-orange-500 hover:bg-orange-600 mt-2"
                  onClick={() => navigate('/dashboard/events/create')}
                >
                  Create an event
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
